/**
 * Organization scope — the single authority for "which organization is the client
 * currently showing, and what has to happen the moment that changes".
 *
 * WHY THIS EXISTS
 * ---------------
 * The active organization lives in `authStore_v1.selectedOrg`, and switching orgs
 * used to be a one-line field swap. Everything else in the client — two dozen
 * Zustand singletons, a global activity Map, live SSE handles, persisted
 * localStorage blobs — was created at module load and lived until the tab closed.
 * Nothing observed the switch. The only defence was a pair of hand-written
 * `reset()` calls inside the org dropdown, which is a defence that lasts exactly
 * until the next store is added.
 *
 * That is the root cause of every cross-org leak: org-scoped state with
 * process-scoped lifetime. This module gives that state a lifetime.
 *
 * THE MODEL
 * ---------
 *   authStore.selectedOrg  ──(bridge)──▶  orgScope  ──▶  registered state
 *        (source of truth)                (lifetime)      (partition or discard)
 *
 * `orgScope` is deliberately NOT a second source of truth. It mirrors the auth
 * store (see `bridge.ts`) and its job is sequencing: on a switch it bumps the
 * epoch, publishes the new org, then fans out synchronously to every registered
 * piece of state. Synchronous matters — the fan-out completes inside the same
 * Zustand `set()` that changed `selectedOrg`, so React re-renders once, with a
 * consistent world. There is no frame in which Org B's header is drawn over Org
 * A's runs.
 *
 * Registration is lazy by design: a store that was never imported holds no state
 * and therefore cannot leak, so there is nothing to tear down for it.
 */

import type { OrgChangeListener, OrgId, OrgScopedState, OrgToken } from './types';

// ============================================================================
// State
// ============================================================================

let activeOrgId: OrgId | null = null;

/**
 * Bumped on every switch, including a switch back to a previously-active org.
 * `OrgToken` comparisons use it so an A → B → A round trip correctly invalidates
 * work started during the first visit to A.
 */
let epoch = 0;

/** Registered state, in registration order. `Map` keyed by name makes re-registration idempotent. */
const registry = new Map<string, OrgScopedState>();

/** Ad-hoc subscribers (React effects, the query-cache bridge) that aren't stores. */
const listeners = new Set<OrgChangeListener>();

// ============================================================================
// Reads
// ============================================================================

/** The organization currently being shown, or null when signed out / not yet loaded. */
export function getActiveOrgId(): OrgId | null {
  return activeOrgId;
}

/** Current switch epoch. Exposed for diagnostics and tests. */
export function getOrgEpoch(): number {
  return epoch;
}

/**
 * True when `orgId` is the organization currently being shown.
 *
 * Use for cheap, non-async checks. For anything that spans an `await`, capture an
 * {@link OrgToken} instead — an org can be left and re-entered while you wait,
 * and this function cannot tell those two visits apart.
 */
export function isActiveOrg(orgId: OrgId | null | undefined): boolean {
  return !!orgId && orgId === activeOrgId;
}

// ============================================================================
// Registration
// ============================================================================

/**
 * Register a piece of org-scoped state.
 *
 * Call once at module scope, next to the store it protects, so the store cannot
 * be imported without its teardown coming with it.
 *
 * @returns An unregister function (used by tests; production code registers for
 *          the lifetime of the tab).
 */
export function registerOrgScopedState(state: OrgScopedState): () => void {
  registry.set(state.name, state);
  return () => {
    if (registry.get(state.name) === state) registry.delete(state.name);
  };
}

/**
 * Convenience wrapper for the common case: state that is simply discarded when
 * the organization changes.
 *
 * Correct for ephemeral view state — drafts, filters, selections, wizards. NOT
 * correct for anything the user would expect to find again when they switch
 * back; partition that by org instead (see `activityStore`, `agentRunStore`).
 */
export function registerOrgScopedReset(name: string, reset: () => void): () => void {
  return registerOrgScopedState({
    name,
    onOrgChange: () => {
      reset();
    },
  });
}

/** Subscribe to organization changes without registering state. */
export function subscribeToOrgChange(listener: OrgChangeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Registered state names, in registration order. Diagnostics and tests. */
export function getRegisteredOrgScopedStateNames(): string[] {
  return [...registry.keys()];
}

// ============================================================================
// Writes
// ============================================================================

/**
 * Publish the new active organization and tear down / re-point everything scoped
 * to the old one.
 *
 * Only `bridge.ts` (and tests) may call this. Every other caller should change
 * `authStore_v1.selectedOrg`, which is the source of truth.
 *
 * A handler that throws must not stop the remaining handlers: a half-torn-down
 * client is precisely the leak this module exists to prevent, so failures are
 * logged and the fan-out continues.
 */
export function setActiveOrgId(nextOrgId: OrgId | null): void {
  const previousOrgId = activeOrgId;
  if (previousOrgId === nextOrgId) return;

  activeOrgId = nextOrgId;
  epoch += 1;

  // Registered state first: stores re-point their org-scoped views before any
  // listener (e.g. the query-cache purge) or React render observes the change.
  for (const state of registry.values()) {
    try {
      state.onOrgChange(nextOrgId, previousOrgId);
    } catch (error) {
      console.error(`[orgScope] "${state.name}" failed to handle org change`, error);
    }
  }

  for (const listener of listeners) {
    try {
      listener(nextOrgId, previousOrgId);
    } catch (error) {
      console.error('[orgScope] org-change listener failed', error);
    }
  }
}

/**
 * Tear down every registered piece of org-scoped state because the session
 * ended.
 *
 * Called by `bridge.ts` when the auth store drops its token and user. Distinct
 * from `setActiveOrgId(null)`, which also fires on sign-out: that one only says
 * "stop showing this org", while this one says "this state has no owner — drop
 * it, including the organizations that are merely parked".
 */
export function signOutOrgScopedState(): void {
  for (const state of registry.values()) {
    try {
      state.onSignOut?.();
    } catch (error) {
      console.error(`[orgScope] "${state.name}" failed to handle sign-out`, error);
    }
  }
}

// ============================================================================
// Async safety
// ============================================================================

/**
 * Capture the current scope before starting async work.
 *
 * ```ts
 * const token = captureOrgToken();
 * const result = await fetchThing();
 * if (!isOrgTokenCurrent(token)) return;   // Org A's answer, Org B's screen
 * set({ thing: result });
 * ```
 */
export function captureOrgToken(): OrgToken {
  return { orgId: activeOrgId, epoch };
}

/** True when `token` still describes the scope on screen. */
export function isOrgTokenCurrent(token: OrgToken): boolean {
  return token.epoch === epoch && token.orgId === activeOrgId;
}

/**
 * Wrap a callback so it becomes a no-op once the organization has moved on.
 *
 * Built for callbacks you hand to something that will keep calling them — SSE
 * `onEvent`, subscription handlers, `setInterval` ticks — where checking at the
 * call site would mean checking at every call site.
 */
export function withOrgGuard<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  token: OrgToken = captureOrgToken(),
): (...args: TArgs) => void {
  return (...args: TArgs) => {
    if (!isOrgTokenCurrent(token)) return;
    callback(...args);
  };
}

// ============================================================================
// Tests
// ============================================================================

/**
 * Restore the module to its just-imported state.
 *
 * Clears the registry too, so a test that imports a store module gets that
 * store's registration back only by re-importing it. Tests that need both should
 * reset scope first, then import.
 */
export function __resetOrgScopeForTests(): void {
  activeOrgId = null;
  epoch = 0;
  registry.clear();
  listeners.clear();
}

/** Set the active org without a fan-out. For arranging test preconditions only. */
export function __setActiveOrgIdSilentlyForTests(orgId: OrgId | null): void {
  activeOrgId = orgId;
}
